import { React, useEffect, useState, useContext, useRef, useMemo, useCallback } from 'react';
import { create } from 'ionicons/icons';
import { IonContent, IonList, IonCard, IonCardContent, IonButton, IonIcon } from '@ionic/react';
import { AppContext } from '../../../../global/AppContext';
import { acceptFight, TAKEOUT_TYPES } from '../../../../global/request/bet';
import { getRestaurant } from '../../../../global/request/restaurant';
import { getPastAddresses } from '../../../../global/request/user';
import {
  areLocationsInRange,
  calculateGrandTotal,
  DELIVERY_RANGE,
  getAddressLocation,
  parseFormattedAddress,
} from '../../../../global/util';
import useGoogleMapsLoader from '../../../../googleMapsApi/useGoogleMapsLoader';
import AddressFields from '../../../addressFields/AddressFields';
import { AuthContext } from '../../../authentication/AuthContext';
import BaseFightCard from '../../../fightCardV2.1.0/BaseFightCard';
import AddressCompactCard from '../AddressCompactCard';
import OverLimitText from '../OverLimitText';
import PriceBreakdownCard from '../PriceBreakdownCard';
import TakerItemCard from '../TakerItemCard';
import TakerModalFooter from '../TakerModalFooter';
import TakerModalHeader from '../TakerModalHeader';
import '../../../../styles/fight/takerProcess/FinalizeFight.scss';

const FinalizeFight = ({
  fightData,
  menuSelections,
  takerTotalPrice,
  makerTotalPrice,
  visitedMenu,
  closeModal,
  notificationClose,
  saveMenuChanges,
  goToModifyMenu,
  editItems,
  onNext,
  onComplete,
  paymentId,
  changedAddress,
  changedAddressLatitude,
  changedAddressLongitude,
  changedAddressIsAutocompleted,
  saveAddressChange,
  showErrorPopup,
  optionalTakerVenue,
  onAcceptFight,
  className,
}) => {
  const authCtx = useContext(AuthContext);
  const bearerToken = authCtx.tokens.idToken;
  const [restaurant, setRestaurant] = useState([]);
  const editedMenuAmountsRef = useRef(menuSelections);
  const makerItems = fightData.restaurantItems?.filter(item => item.maker_item) || [];
  const [editedTotalPrice, setEditedTotalPrice] = useState(takerTotalPrice);

  const appCtx = useContext(AppContext);

  const [isAddressValid, setIsAddressValid] = useState();
  const [addressLine, setAddressLine] = useState(appCtx.deliveryAddress || '');
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [addressDetails, setAddressDetails] = useState({
    streetAddress: '',
    apt: '',
    city: '',
    state: '',
    zip: '',
  });
  const [addressLatitude, setAddressLatitude] = useState(0);
  const [addressLongitude, setAddressLongitude] = useState(0);
  const [isAddressAutocompleted, setIsAddressAutocompleted] = useState(false);
  const [pastAddresses, setPastAddresses] = useState([]);
  const deliveryInstructionsRef = useRef();

  const { isLoaded } = useGoogleMapsLoader();
  const geocoderRef = useRef();

  const isFightDelivery = fightData.takeoutType === TAKEOUT_TYPES.DELIVERY;

  // Totals are in cents
  const makerGrandTotal = useMemo(
    () => calculateGrandTotal(makerTotalPrice, isFightDelivery),
    [makerTotalPrice, isFightDelivery],
  );

  const formatName = name => {
    const names = name.split(' ');
    return `${names[0]} ${names[names.length - 1][0]}.`;
  };

  const isCurrentUserMaker = authCtx.user?.name === fightData.makerName;

  const getGamePanelData = () => {
    // Maker = right side; taker = left side
    if (isCurrentUserMaker) {
      return {
        sportLeague: fightData.sport,
        makerName: formatName(fightData.takerName),
        makerTeam: fightData.takerTeam,
        makerTeamAcronym: fightData.makerteamAcronym,
        makerTeamColor: fightData.takerOutcomeTeamColor,
        makerOutcomePhrase: fightData.makerOutcomePhrase.replace(fightData.makerOutcomeTeam, ''),
        takerTeam: fightData.makerTeam,
        takerTeamAcronym: fightData.makerTeamAcronym,
        takerTeamColor: fightData.makerOutcomeTeamColor,
        makerItems,
      };
    }

    return {
      sportLeague: fightData.sport,
      makerName: formatName(fightData.makerName),
      makerTeam: fightData.makerTeam,
      makerTeamAcronym: fightData.makerTeamAcronym,
      makerTeamColor: fightData.makerOutcomeTeamColor,
      makerOutcomePhrase: fightData.takerOutcomePhrase.replace(fightData.takerOutcomeTeam, ''),
      takerTeam: fightData.takerTeam,
      takerTeamAcronym: fightData.takerTeamAcronym,
      takerTeamColor: fightData.takerOutcomeTeamColor,
      makerItems,
    };
  };

  const gamePanelData = getGamePanelData();

  const handleSetAddress = () => {
    if (!isAddressValid) {
      console.error('address in invalid format');
      return;
    }

    const trimmedApt = addressDetails.apt?.replace(' ', '');
    const formattedAddress = `${addressDetails.streetAddress}${trimmedApt ? ` ${trimmedApt}` : ''}, ${
      addressDetails.city
    }, ${addressDetails.state} ${addressDetails.zip}`;

    setAddressLine(formattedAddress);

    setShowAddressFields(false);
  };

  const handleLocationSelect = location => {
    setAddressLatitude(location.lat);
    setAddressLongitude(location.lng);
  };

  const getRestaurantInfo = useCallback(async () => {
    const venueId = optionalTakerVenue ? optionalTakerVenue.id : fightData.restaurantId;
    try {
      const response = await getRestaurant(bearerToken, venueId);
      setRestaurant(response);
    } catch (error) {
      console.error('[FinalizeFight] Error fetching restaurant info:', error);
    }
  }, [optionalTakerVenue, fightData.restaurantId, bearerToken, setRestaurant]);

  const changePage = nextPageFunc => {
    saveMenuChanges(editedMenuAmountsRef.current, editedTotalPrice);
    saveAddressChange(addressLine, addressLatitude, addressLongitude, isAddressAutocompleted);
    nextPageFunc();
  };

  const changeItemAmount = (itemId, newAmount) => {
    const currentSelections = [...editedMenuAmountsRef.current];
    const itemIndex = currentSelections.findIndex(item => item.id === itemId);

    if (itemIndex === -1) {
      console.warn('[FinalizeFight] Item not found with ID:', itemId);
      return;
    }

    const changedItem = currentSelections[itemIndex];
    const oldAmount = changedItem.amount;
    const diff = newAmount - oldAmount;

    if (newAmount === 0) {
      currentSelections.splice(itemIndex, 1);
      const newTotal = editedTotalPrice - oldAmount * changedItem.price;
      editedMenuAmountsRef.current = currentSelections;
      setEditedTotalPrice(newTotal);
      saveMenuChanges(currentSelections, newTotal);
    } else {
      currentSelections[itemIndex].amount = newAmount;
      const newTotal = editedTotalPrice + diff * changedItem.price;
      editedMenuAmountsRef.current = currentSelections;
      setEditedTotalPrice(newTotal);
      saveMenuChanges(currentSelections, newTotal);
    }
  };

  useEffect(() => {
    getRestaurantInfo();
  }, [fightData.restaurantId, bearerToken, optionalTakerVenue, getRestaurantInfo]);

  useEffect(() => {
    const initialAddressInfo = {
      addressLine: '',
      latitude: 0,
      longitude: 0,
      isAutocompleted: false,
    };
    if (changedAddress) {
      initialAddressInfo.addressLine = changedAddress;
      initialAddressInfo.latitude = changedAddressLatitude;
      initialAddressInfo.longitude = changedAddressLongitude;
      initialAddressInfo.isAutocompleted = changedAddressIsAutocompleted;
    } else {
      initialAddressInfo.addressLine = appCtx.deliveryAddress || '';
      initialAddressInfo.latitude = appCtx.deliveryLatitude;
      initialAddressInfo.longitude = appCtx.deliveryLongitude;
      initialAddressInfo.isAutocompleted = appCtx.isAddressAutocompleted;
    }

    setAddressLine(initialAddressInfo.addressLine);

    const addrDetails = parseFormattedAddress(initialAddressInfo.addressLine);

    if (!addrDetails.invalid) {
      setAddressDetails(addrDetails);
      setIsAddressValid(true);
    } else {
      setIsAddressValid(false);
    }

    setAddressLatitude(initialAddressInfo.latitude);
    setAddressLongitude(initialAddressInfo.longitude);
    setIsAddressAutocompleted(initialAddressInfo.isAutocompleted);
  }, [
    appCtx.deliveryAddress,
    changedAddress,
    changedAddressLatitude,
    changedAddressLongitude,
    changedAddressIsAutocompleted,
    appCtx.deliveryLatitude,
    appCtx.deliveryLongitude,
    appCtx.isAddressAutocompleted,
  ]);

  useEffect(() => {
    const asyncHelper = async () => {
      const res = await getPastAddresses(authCtx.tokens.idToken);
      setPastAddresses(res);
    };

    try {
      asyncHelper();
    } catch (e) {
      console.error('Failed to fetch past addresses', e);
    }
  }, [authCtx.tokens]);

  useEffect(() => {
    if (isLoaded) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  const closeModalComplete = () => {
    changePage(onComplete);

    if (typeof notificationClose === 'function') {
      notificationClose();
    }
  };

  const finalizeFightRequest = async () => {
    if (!paymentId && (takerTotalPrice !== 0 || takerTotalPrice === undefined) && makerTotalPrice !== 0) {
      console.warn('[FinalizeFight] finalizeFightRequest: no paymentId');
      showErrorPopup?.('No Payment found. Please complete payment first.');
      return;
    }

    if (
      fightData.takeoutType === TAKEOUT_TYPES.DELIVERY &&
      restaurant.name === 'Jets Pizza' &&
      editedTotalPrice < 1000
    ) {
      console.warn(
        '[FinalizeFight] finalizeFightRequest: Jets Pizza delivery is only available for subtotals of $10.00 or more',
      );
      showErrorPopup?.('Delivery at this venue is only available for subtotals of $10.00 or more');
      return;
    }

    try {
      const formattedTakerItems = menuSelections
        ? menuSelections.map(item => {
            return {
              menu_item_id: typeof item.id === 'number' ? item.id : -1,
              product_id: typeof item.id === 'string' ? item.id : '',
              quantity: item.amount ?? 1,
              price: item.price ?? 0,
              name: item.name ?? 'Unknown Item',
            };
          })
        : [];

      const deliveryCoords = {
        lat: addressLatitude,
        lng: addressLongitude,
        isReliable: isAddressAutocompleted,
      };

      if (fightData.takeoutType === TAKEOUT_TYPES.DELIVERY) {
        if (!isAddressValid) {
          console.warn('[FinalizeFight] finalizeFightRequest: invalid address');
          showErrorPopup?.('Invalid address format. Please change your address.');
          return;
        }
        if (!isLoaded) {
          console.error('Google Maps API is not loaded.');
          return;
        }
        if (!deliveryCoords.isReliable) {
          const deliveryLocation = await getAddressLocation(geocoderRef.current, addressLine);
          if (deliveryLocation === null) {
            console.warn('[FinalizeFight] finalizeFightRequest: could not get delivery location');
            deliveryCoords.isReliable = false;
          } else {
            deliveryCoords.isReliable = true;
            deliveryCoords.lat = deliveryLocation.lat();
            deliveryCoords.lng = deliveryLocation.lng();
          }
        }
        if (!deliveryCoords.isReliable) {
          console.warn('[FinalizeFight] finalizeFightRequest: geolocation is not reliable. Skipping range check.');
        } else {
          const restaurantLocation = await getAddressLocation(geocoderRef.current, restaurant.address);
          if (restaurantLocation === null) {
            console.warn(
              '[FinalizeFight] finalizeFightRequest: could not get restaurant geolocation. Skipping range check.',
            );
          } else if (
            !areLocationsInRange(
              new window.google.maps.LatLng(deliveryCoords.lat, deliveryCoords.lng),
              restaurantLocation,
              DELIVERY_RANGE,
            )
          ) {
            console.warn('[FinalizeFight] finalizeFightRequest: delivery address is not in restaurant delivery range');
            showErrorPopup?.("Your address is outside this restaurant's delivery range.");
            return;
          }
        }
      }

      if (onAcceptFight) {
        await onAcceptFight(
          fightData.id,
          paymentId ?? '',
          addressLine,
          formattedTakerItems,
          deliveryInstructionsRef.current?.value || '',
          deliveryCoords.lat,
          deliveryCoords.lng,
        );
      } else {
        await acceptFight(
          bearerToken,
          fightData.id,
          paymentId ?? '',
          addressLine,
          formattedTakerItems,
          deliveryInstructionsRef.current?.value || '',
          deliveryCoords.lat,
          deliveryCoords.lng,
        );
      }

      closeModalComplete();
    } catch (error) {
      console.error('[FinalizeFight] finalizeFightRequest error:', error);
      showErrorPopup?.('Failed to finalize fight. Please try again.');
    }
  };

  const goToPaymentStep = () => {
    if (editedTotalPrice === 0) {
      finalizeFightRequest();
    } else {
      changePage(onNext);
    }
  };

  const closeModalX = () => {
    closeModal(false);
    if (typeof notificationClose === 'function') {
      notificationClose();
    }
  };

  return (
    <div className={className} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TakerModalHeader
        pageName='Summary Cart'
        showBackButton
        onBack={() => changePage(goToModifyMenu)}
        onXButton={() => closeModalX(false)}
      />
      <IonContent scrollY className='summary-content' style={{ flex: 1 }}>
        <BaseFightCard fight={gamePanelData} tabComponent={fightData.sport} />
        {visitedMenu ? (
          <>
            <div className='section-header-wrapper'>
              <h3 className='selection-name outcome-name'>If you win</h3>
            </div>
            <IonList className='menu-list summary'>
              {menuSelections.map(item => (
                <TakerItemCard key={item.id} item={item} onAmountChange={amount => changeItemAmount(item.id, amount)} />
              ))}
            </IonList>
            <div className='modify-item'>
              <IonIcon icon={create} />
              <button type='button' onClick={goToModifyMenu} className='change-text'>
                Edit Item
              </button>
            </div>
            <hr className='item-wrapper-separator' />
            <IonCard className='total-card'>
              <IonCardContent className='total-card-content'>
                <p className='total-text'>{fightData.makerName} PAYS: </p>
                <div className='budget-indicator'>
                  {editedTotalPrice <= makerTotalPrice ? (
                    <span className='budget-check'>In budget</span>
                  ) : (
                    <span className='budget-warning'>Over budget</span>
                  )}
                </div>
                <p className={`total-price ${editedTotalPrice > makerTotalPrice ? 'over-budget-price' : ''}`}>
                  ${(editedTotalPrice / 100).toFixed(2)}
                </p>
              </IonCardContent>
            </IonCard>
            {!isCurrentUserMaker && (
              <>
                {OverLimitText(editedTotalPrice, makerTotalPrice)}
                <div className='section-header-wrapper'>
                  <h3 className='selection-name outcome-name'>If {fightData.makerName} wins</h3>
                </div>
                <IonList className='menu-list summary oponent-items'>
                  {makerItems.map(item => (
                    <TakerItemCard key={item.product_id || item.menu_item_id} item={item} isNotEditable />
                  ))}
                </IonList>
              </>
            )}
            <PriceBreakdownCard
              subtotal={makerTotalPrice}
              isDelivery={false}
              total={makerGrandTotal}
              totalLabel='TOTAL'
              disclaimer={
                isFightDelivery
                  ? "*Sales tax amount is a temporary hold based on the highest sales tax rate in the US. You will only be charged for the sales tax rate in your opponent's city if your opponent wins."
                  : ''
              }
            />
          </>
        ) : (
          <>
            {editItems ? (
              <>
                <div className='section-header-wrapper'>
                  <h3 className='selection-name outcome-name'>If you win</h3>
                </div>
                <IonList className='menu-list summary'>
                  {menuSelections.map(item => (
                    <TakerItemCard
                      key={item.id}
                      item={item}
                      onAmountChange={amount => changeItemAmount(item.id, amount)}
                    />
                  ))}
                </IonList>
                <div className='modify-item'>
                  <IonIcon icon={create} />
                  <button type='button' onClick={goToModifyMenu} className='change-text'>
                    Edit Item
                  </button>
                </div>
                <hr className='item-wrapper-separator' />
                <IonCard className='total-card'>
                  <IonCardContent className='total-card-content'>
                    <p className='total-text'>{fightData.makerName} PAYS: </p>
                    <div className='budget-indicator'>
                      {editedTotalPrice <= makerTotalPrice ? (
                        <span className='budget-check'>In budget</span>
                      ) : (
                        <span className='budget-warning'>Over budget</span>
                      )}
                    </div>
                    <p className={`total-price ${editedTotalPrice > makerTotalPrice ? 'over-budget-price' : ''}`}>
                      ${(editedTotalPrice / 100).toFixed(2)}
                    </p>
                  </IonCardContent>
                </IonCard>
              </>
            ) : (
              <>
                {' '}
                <div className='section-header-wrapper'>
                  <h3 className='selection-name outcome-name'>If you win</h3>
                </div>
                <div className='edit-item'>
                  <IonButton type='button' onClick={goToModifyMenu} size='small' className='edit-item-button'>
                    Edit Items
                  </IonButton>
                </div>
              </>
            )}

            {!isCurrentUserMaker && (
              <>
                {OverLimitText(editedTotalPrice, makerTotalPrice)}
                <div className='section-header-wrapper'>
                  <h3 className='selection-name outcome-name'>If {fightData.makerName} wins</h3>
                </div>
                <IonList className='menu-list summary oponent-items'>
                  {makerItems.map(item => (
                    <TakerItemCard key={item.product_id || item.menu_item_id} item={item} isNotEditable />
                  ))}
                </IonList>
              </>
            )}
            <PriceBreakdownCard
              subtotal={makerTotalPrice}
              isDelivery={false}
              total={makerGrandTotal}
              totalLabel='TOTAL'
              disclaimer={
                isFightDelivery
                  ? "*Sales tax amount is a temporary hold based on the highest sales tax rate in the US. You will only be charged for the sales tax rate in your opponent's city if your opponent wins."
                  : ''
              }
            />
          </>
        )}
        <div className='bottom-info'>
          *The payment is only completed if you are on the loser side and the items will be delivered to the winner.
          Delivery is provided by a third party service.{' '}
        </div>
        {fightData.takeoutType === TAKEOUT_TYPES.DELIVERY && (
          <div className='user-address-section'>
            {!showAddressFields && (
              <AddressCompactCard
                isAddressValid={isAddressValid}
                addressLine={addressLine}
                onEdit={() => setShowAddressFields(true)}
              />
            )}
            {showAddressFields && (
              <>
                <AddressFields
                  addressDetails={addressDetails}
                  setAddressDetails={setAddressDetails}
                  setIsFormValid={setIsAddressValid}
                  showAutocompleteDefault={false}
                  pastAddresses={pastAddresses}
                  onLocationSelect={handleLocationSelect}
                  setIsAddressAutocompleted={setIsAddressAutocompleted}
                />
                <IonButton expand='block' onClick={handleSetAddress} className='set-address-button'>
                  Set Address
                </IonButton>
              </>
            )}
            <textarea
              placeholder="Delivery instructions. i.e. 'Leave at door'"
              ref={deliveryInstructionsRef}
              className='delivery-instructions-textbox'
            />
          </div>
        )}
      </IonContent>

      <TakerModalFooter
        buttonName={editedTotalPrice === 0 ? 'Finalize' : 'Add payment'}
        onClick={goToPaymentStep}
        isDisabled={editedTotalPrice > makerTotalPrice}
      />
    </div>
  );
};

export default FinalizeFight;
